import type { NextPage } from 'next';
import { useTranslation, TFunction } from 'next-i18next/pages';
import { useEffect } from 'react';

import { JobPosition } from '@pflegia/types/api/job-position/job-position';
import { SourceDomain } from '@pflegia/types/api/source-domain';
import { UtmReentrySource } from '@pflegia/types/common/enums';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { ExternalHeader } from '@pflegia/ui/shared/external-header/ExternalHeader';
import NextSeo from '@pflegia/ui/shared/next-seo';
import { getPflegiaDefaultDomainName } from '@pflegia/ui/shared/utils/get-pflegia-default-domain';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import { useParseJobPositionForPerks } from '@hooks/get-job-position.hook';

import { JOB_BOARD, SLIMMED_DOWN_FUNNEL } from '@shared/application/constants';

import JobDetail from 'domains/job-detail';

export { getServerSideProps } from '@domains/job-detail/utils/get-server-side-props-external';

interface ExternalJobDetailPageProps {
  jobPosition: JobPosition;
  validThrough: string;
}

const getSeoProps = (jobPosition: JobPosition, appName: string, t: TFunction) => {
  const title = {
    jobTitle: jobPosition?.title,
    location: jobPosition?.geoLocation?.city || t('jobDetails:seo.locationFallback'),
    appName,
  };

  const description = {
    jobTitle: jobPosition?.title,
    employerName: jobPosition?.employer?.name,
  };

  return {
    title: t('jobDetails:seo.rotatingTitles.0', title),
    description: jobPosition.employer.formalLanguage
      ? t('jobDetails:seo.formal.description', description)
      : t('jobDetails:seo.description', description),
  };
};

const ExternalJobDetailPage: NextPage<ExternalJobDetailPageProps> = ({ jobPosition, validThrough }) => {
  const { perks, highlights } = useParseJobPositionForPerks(jobPosition);
  const { sourceDomainName: appName } = useFlavorFromRoute();
  const authenticated = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const seoProps = getSeoProps(jobPosition, appName, t);
  const employerLogoPath = getPublicImageUrl(jobPosition?.employer?.logoKey) || '';
  const canonicalDomain = getPflegiaDefaultDomainName(jobPosition.employer.sourceDomains as SourceDomain[]);

  useEffect(() => {
    if (!authenticated) cookieStorage.setItem('reentry_source', UtmReentrySource.Multiposting);
  }, [authenticated]);

  return (
    <>
      <NextSeo
        defaultDomain={SourceDomain.PflegiaDe}
        flavorDomain={canonicalDomain}
        title={seoProps.title}
        titleTemplate='%s'
        description={seoProps.description}
        nofollow
        noindex
        openGraph={{
          locale: language || 'de_DE',
          type: 'basic',
          title: seoProps.title,
          description: seoProps.description,
          defaultImageHeight: 1200,
          defaultImageWidth: 630,
          images: [
            {
              url: employerLogoPath,
            },
          ],
        }}
      />
      <ExternalHeader
        links={[
          { children: t('funnel:externalHeader.seeSimilarJobs'), href: JOB_BOARD, key: JOB_BOARD },
          {
            children: t('funnel:externalHeader.signUp'),
            // eslint-disable-next-line eslint-compat/camelcase
            href: { pathname: SLIMMED_DOWN_FUNNEL, query: { jobId: jobPosition.id, reentry_source: UtmReentrySource.Multiposting } },
            key: SLIMMED_DOWN_FUNNEL,
            useJsNavigation: true,
          },
        ]}
        logoSrc={employerLogoPath}
        name={jobPosition.employer.name}
        appName={jobPosition.employer.sourceDomains[0]}
      />
      <JobDetail jobPosition={jobPosition} highlights={highlights} perks={perks} validThrough={validThrough} />
    </>
  );
};

export default ExternalJobDetailPage;
